import { ReactElement } from "react";
import { Typography, Button } from "@mui/material";
import Workflow from "./Workflow.component";
import { useProjectStore } from "../stores/project.store";
import { getWorkflowConfiguration } from "../config/workflow.config";

export interface WorkflowPanelProps {
  fileCount: number;
  selectedFiles: string[];
  uploadedFileNames: string[];
  onFileSelect: (fileName: string, checked: boolean) => void;
  onSelectAllFiles: (checked: boolean) => void;
  onRunSelectedFiles: () => void;
  hasStartedAnalysis: boolean;
  onStepSelect?: (stepId: string) => void;
  completedSteps?: string[];
  className?: string;
}

const WorkflowPanel = ({
  fileCount,
  selectedFiles,
  uploadedFileNames,
  onFileSelect,
  onSelectAllFiles,
  onRunSelectedFiles,
  hasStartedAnalysis,
  onStepSelect,
  completedSteps = [],
  className = "",
}: WorkflowPanelProps): ReactElement | null => {
  const { activeStep, setActiveStep } = useProjectStore();
  const workflowStepCount = getWorkflowConfiguration("single-cell").steps.length;
  const progressPercent = Math.min(
    100,
    Math.round((completedSteps.length / Math.max(workflowStepCount, 1)) * 100),
  );

  const handleStepSelect = (stepId: string) => {
    setActiveStep(stepId);
    onStepSelect?.(stepId);
  };
  if (fileCount === 0) {
    return null;
  }

  if (uploadedFileNames.length === 0) {
    return (
      <div className={`flex flex-col h-full p-6 ${className}`}>
        <div className="flex flex-col items-center justify-center text-center flex-1">
          <Typography
            variant="h6"
            className="text-gray-500 mb-2"
            sx={{ fontWeight: 500 }}
          >
            No files uploaded yet
          </Typography>
          <Typography variant="body2" className="text-gray-400">
            Upload files to start your analysis
          </Typography>
        </div>
      </div>
    );
  }

  return (
    <div className={`flex flex-col h-full p-6 ${className}`}>
      {!hasStartedAnalysis && !activeStep ? (
        // File Selection View
        <div className="flex flex-col h-full">
          <Typography
            variant="h5"
            className="text-gray-900 mb-2"
            sx={{ fontWeight: 600 }}
          >
            Select Files
          </Typography>
          <Typography variant="body2" className="text-gray-600 mb-4">
            Choose files to run analysis on. {uploadedFileNames.length} file(s)
            available.
          </Typography>

          {/* Select All Checkbox */}
          <div className="mb-4 flex items-center justify-between">
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={
                  selectedFiles.length === uploadedFileNames.length &&
                  uploadedFileNames.length > 0
                }
                onChange={(e) => onSelectAllFiles(e.target.checked)}
                className="w-4 h-4 text-[#19C1DB] border-gray-300 rounded focus:ring-[#19C1DB]"
              />
              <Typography variant="body2" className="text-gray-700">
                Select all uploaded files ({uploadedFileNames.length})
              </Typography>
            </label>
            {selectedFiles.length > 0 && (
              <Button
                variant="text"
                size="small"
                onClick={() => onSelectAllFiles(false)}
                className="text-red-500 hover:text-red-700 hover:bg-red-50"
                sx={{ textTransform: "none" }}
              >
                Clear All
              </Button>
            )}
          </div>

          {/* File List */}
          <div className="flex-1 overflow-y-auto mb-4">
            <div className="space-y-2">
              {uploadedFileNames.map((fileName, i) => {
                const isSelected = selectedFiles.includes(fileName);
                return (
                  <div
                    key={i}
                    className="flex items-center gap-3 p-3 bg-white rounded-lg border border-gray-200"
                  >
                    <input
                      type="checkbox"
                      checked={isSelected}
                      onChange={(e) => onFileSelect(fileName, e.target.checked)}
                      className="w-4 h-4 text-[#19C1DB] border-gray-300 rounded focus:ring-[#19C1DB]"
                    />
                    <Typography
                      variant="body2"
                      className="flex-1 text-gray-700"
                    >
                      {fileName}
                    </Typography>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Run Selected Files Button */}
          <Button
            variant="contained"
            onClick={onRunSelectedFiles}
            disabled={selectedFiles.length === 0}
            className="bg-[#19C1DB] hover:bg-[#17B1CB] text-white font-semibold py-3"
            sx={{
              textTransform: "none",
              fontSize: "1rem",
              borderRadius: "8px",
            }}
          >
            Run Selected Files ({selectedFiles.length})
          </Button>
        </div>
      ) : hasStartedAnalysis || activeStep ? (
        // Analysis Workflow View
        <div className="flex flex-col h-full">
          <Typography
            variant="h5"
            className="text-gray-900 mb-2"
            sx={{ fontWeight: 600 }}
          >
            Complete analysis
          </Typography>
          <Typography variant="body2" className="text-gray-600 mb-4">
            Perform all necessary parts of analysis to filter data.
          </Typography>

          {/* Progress Indicator */}
          <div className="mb-6">
            <div className="flex items-center justify-between mb-2">
              <Typography variant="body2" className="text-gray-600">
                {progressPercent}%
              </Typography>
            </div>
            <div className="flex gap-1">
              {Array.from({ length: workflowStepCount }).map((_, index) => (
                <div
                  key={index}
                  className={`h-2 rounded flex-1 ${
                    index < completedSteps.length
                      ? "bg-[#19C1DB]"
                      : "bg-gray-300"
                  }`}
                />
              ))}
            </div>
          </div>

          {/* Analysis Steps */}
          <div className="flex-1 overflow-y-auto">
            <Workflow
              activeStep={activeStep || undefined}
              onStepSelect={handleStepSelect}
              completedSteps={completedSteps}
            />
          </div>
        </div>
      ) : (
        // Fallback - should not reach here normally
        <div className="flex flex-col h-full">
          <Typography variant="body2" className="text-gray-500 text-center">
            Ready to start analysis
          </Typography>
        </div>
      )}
    </div>
  );
};

export default WorkflowPanel;
